import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { seo, seoLinks } from "~/utils/seo";
import { PricingHero } from "~/components/pricing/PricingHero";
import { PricingPageShell } from "~/components/pricing/PricingPageShell";

export const Route = createFileRoute("/pricing")({
  loader: () => ({}),
  head: () => ({
    meta: seo({
      title: "C1 pricing — Platform and Flex pricing models",
      description:
        "Compare predictable, SKU-based C1 Platform pricing with Flex usage-based pricing. Configure products, program size, hosting, and support.",
      url: "/pricing",
    }),
    links: seoLinks({ url: "/pricing" }),
  }),
  component: PricingLayout,
});

/**
 * Maps legacy `/pricing#anchor` URLs (used in old blog posts, emails,
 * and search results) to the new route structure. Index-anchored hashes
 * (`#plans`, `#calculator`) collapse to `/pricing` since those sections
 * live on the index. Non-matching hashes are ignored.
 */
const HASH_REDIRECTS: Record<string, string> = {
  "#plans": "/pricing",
  "#calculator": "/pricing",
  "#how-c1us-work": "/pricing",
  "#3-year-cost": "/pricing/value",
  "#vs-per-identity": "/pricing/value",
  "#cost-intelligence": "/pricing/value",
  "#hosting": "/pricing/hosting",
  "#support": "/pricing/support",
  "#enterprise": "/pricing/support",
  "#faq": "/pricing",
};

function PricingLayout() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash) return;
    const target = HASH_REDIRECTS[hash];
    if (!target) return;
    router.navigate({ to: target, replace: true });
  }, [router]);

  return (
    <div data-pricing-page>
      {/*
        Hero + page shell are rendered once at the layout level so they
        stay mounted across sub-route navigation. Only the <Outlet />
        below the rail re-renders, which (combined with the
        scroll-restoration key set in src/router.tsx) gives the
        "content swaps in place, no scroll jump" behavior.
      */}
      <PricingHero />
      <PricingPageShell>
        <Outlet />
      </PricingPageShell>
    </div>
  );
}
